import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Store, Package, ShoppingCart, TrendingUp, Plus, Edit, Eye,
  DollarSign, AlertTriangle, Clock, CheckCircle, Mail, Bell,
  LogOut, Settings, BarChart3, Calendar, Euro, MessageSquare, CreditCard, Trash2, Check, Square, Palette, Crown, Upload
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import BulkActionsToolbar from '../components/admin/BulkActionsToolbar';
import useAuthStore from '../store/authStore';
import axiosInstance from '../api/axios';
import { toast } from 'sonner';
import { formatEUR } from '../utils/format';

const VendorDashboard = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, isHydrated, forceHydrate } = useAuthStore();
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [commissionStatus, setCommissionStatus] = useState(null);
  const [vendorProfile, setVendorProfile] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);

  // Force hydrate on mount
  useEffect(() => {
    forceHydrate();
  }, [forceHydrate]);

  // Auth check
  useEffect(() => {
    if (!isHydrated) return;
    
    if (!isAuthenticated) {
      navigate('/login?redirect=/vendor-dashboard');
      return;
    }
    
    if (user?.role !== 'vendor') {
      toast.error('Nur für Verkäufer zugänglich');
      navigate('/');
      return;
    }
    
    // Check vendor status
    if (user?.vendor_profile?.status !== 'approved') {
      toast.error('Ihr Verkäufer-Account ist noch nicht freigegeben');
      navigate('/');
      return;
    }
    
    loadData();
  }, [isAuthenticated, user, navigate, isHydrated]);

  const loadData = async () => {
    try {
      const [profileRes, statsRes, productsRes, ordersRes, commissionRes, categoriesRes, subRes] = await Promise.all([
        axiosInstance.get('/vendors/me'),
        axiosInstance.get('/vendors/me/stats'),
        axiosInstance.get('/vendors/me/products'),
        axiosInstance.get('/vendors/me/orders'),
        axiosInstance.get('/commissions/my-status').catch(() => ({ data: null })),
        axiosInstance.get('/categories').catch(() => ({ data: [] })),
        axiosInstance.get('/vendor-subscriptions/my-subscription').catch(() => ({ data: null }))
      ]);
      
      setVendorProfile(profileRes.data);
      setStats(statsRes.data);
      setProducts(productsRes.data);
      setOrders(ordersRes.data);
      setCommissionStatus(commissionRes.data);
      setCategories(categoriesRes.data || []);
      setSubscriptionInfo(subRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProduct = (productId) => {
    setSelectedProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map(p => p.id));
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleDeleteProduct = async (productId) => {
    if (!confirm('Möchten Sie dieses Produkt wirklich löschen?')) return;
    
    try {
      await axiosInstance.delete(`/vendors/me/products/${productId}`);
      toast.success('Produkt gelöscht');
      // Reload data
      loadData();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const formatCurrency = (amount) => {
    return `${formatEUR((amount || 0))}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getOrderStatusBadge = (status) => {
    const config = {
      pending: { label: 'Ausstehend', color: 'bg-yellow-100 text-yellow-800' },
      confirmed: { label: 'Bestätigt', color: 'bg-blue-100 text-blue-800' },
      shipped: { label: 'Versendet', color: 'bg-purple-100 text-purple-800' },
      delivered: { label: 'Geliefert', color: 'bg-green-100 text-green-800' },
      cancelled: { label: 'Storniert', color: 'bg-red-100 text-red-800' }
    };
    const c = config[status] || config.pending;
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  if (!isHydrated || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Lädt...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Store className="h-8 w-8 text-purple-600" />
              <div>
                <h1 className="text-lg font-bold text-gray-900">
                  {vendorProfile?.vendor_profile?.shop_name || 'Mein Shop'}
                </h1>
                <p className="text-xs text-gray-500">Verkäufer-Dashboard</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/vendor-dashboard/payment-settings">
                <Button variant="outline" size="sm" className="rounded-none border-[#C4A084] text-[#5D4E3C] hover:bg-[#F5EDE4]">
                  <CreditCard className="h-4 w-4 mr-2" />
                  Zahlungen
                </Button>
              </Link>
              <Link to="/vendor-dashboard/shop-customization">
                <Button 
                  variant={subscriptionInfo?.is_gold ? "default" : "outline"} 
                  size="sm"
                  className={`rounded-none ${subscriptionInfo?.is_gold ? "bg-[#CBAA00] hover:bg-[#B89900] text-white" : "border-[#C4A084] text-[#5D4E3C] hover:bg-[#F5EDE4]"}`}
                >
                  {subscriptionInfo?.is_gold ? (
                    <Crown className="h-4 w-4 mr-2" />
                  ) : (
                    <Palette className="h-4 w-4 mr-2" />
                  )}
                  Farben
                </Button>
              </Link>
              <Link to="/vendor-dashboard/shop-templates">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="rounded-none border-[#C4A084] text-[#5D4E3C] hover:bg-[#F5EDE4]"
                >
                  <Palette className="h-4 w-4 mr-2" />
                  Vorlagen
                </Button>
              </Link>
              {/* Quick Import - only for Gold vendors */}
              {subscriptionInfo?.tier === 'gold' ? (
                <Link to="/vendor-dashboard/import">
                  <Button variant="outline" size="sm" className="rounded-none border-[#CBAA00] text-[#CBAA00] hover:bg-amber-50">
                    <Upload className="h-4 w-4 mr-2" />
                    Schnell-Import
                  </Button>
                </Link>
              ) : (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="opacity-60"
                  onClick={() => toast.info('Schnell-Import ist nur für Gold-Verkäufer verfügbar. Upgrade Sie jetzt!')}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  <Crown className="h-3 w-3 mr-1 text-amber-500" />
                  Import
                </Button>
              )}
              <Link to={`/shop/${user?.id}`} target="_blank">
                <Button variant="default" size="sm" className="bg-[#AF9774] hover:bg-[#9A8569]">
                  <Eye className="h-4 w-4 mr-2" />
                  Zum Shop
                </Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Abmelden
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome & Stats */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Willkommen, {vendorProfile?.first_name}!
          </h2>
          <p className="text-gray-600">
            Hier können Sie Ihre Produkte, Bestellungen und Provisionen verwalten.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 text-center">
              <Package className="h-8 w-8 mx-auto mb-2 text-blue-500" />
              <div className="text-2xl font-bold">{stats?.product_count || 0}</div>
              <div className="text-sm text-gray-600">Produkte</div>
              <div className="text-xs text-green-600">{stats?.active_products || 0} aktiv</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <ShoppingCart className="h-8 w-8 mx-auto mb-2 text-green-500" />
              <div className="text-2xl font-bold">{stats?.total_orders || 0}</div>
              <div className="text-sm text-gray-600">Bestellungen</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Euro className="h-8 w-8 mx-auto mb-2 text-purple-500" />
              <div className="text-2xl font-bold">{formatCurrency(stats?.total_revenue)}</div>
              <div className="text-sm text-gray-600">Gesamtumsatz</div>
            </CardContent>
          </Card>
          <Card className={commissionStatus?.total_owed > 0 ? 'border-orange-400 border-2' : ''}>
            <CardContent className="p-4 text-center">
              <DollarSign className="h-8 w-8 mx-auto mb-2 text-orange-500" />
              <div className="text-2xl font-bold text-orange-600">
                {formatCurrency(commissionStatus?.total_owed)}
              </div>
              <div className="text-sm text-gray-600">Offene Provision</div>
              <div className="text-xs text-gray-500">{commissionStatus?.commission_rate || 8}% Rate</div>
            </CardContent>
          </Card>
        </div>

        {/* Commission Alert */}
        {commissionStatus?.total_owed > 0 && (
          <Card className="mb-6 bg-orange-50 border-orange-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
                <div>
                  <span className="text-orange-800 font-medium">
                    Sie haben offene Provisionen in Höhe von {formatCurrency(commissionStatus.total_owed)}
                  </span>
                  <p className="text-sm text-orange-600">
                    Bitte überweisen Sie den Betrag bis zum Fälligkeitsdatum.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Messages Alert */}
        {commissionStatus?.unread_messages > 0 && (
          <Card className="mb-6 bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <MessageSquare className="h-5 w-5 text-blue-600" />
                <span className="text-blue-800">
                  Sie haben <strong>{commissionStatus.unread_messages}</strong> ungelesene Nachrichten vom Admin
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Gold Upgrade Promo - for Basis vendors */}
        {subscriptionInfo?.tier !== 'gold' && (
          <Card className="mb-6 bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-300">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Crown className="h-6 w-6 text-amber-500" />
                  <div>
                    <span className="text-amber-800 font-semibold">Upgrade auf Gold!</span>
                    <p className="text-sm text-amber-600">
                      Schnell-Import, nur 6% Provision, bis zu 200 Produkte, eigenes Banner
                    </p>
                  </div>
                </div>
                <Link to="/vendor-dashboard/subscription">
                  <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white">
                    Jetzt upgraden
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Content Tabs */}
        <Tabs defaultValue="products" className="space-y-6">
          <TabsList className="grid grid-cols-4 w-full max-w-md">
            <TabsTrigger value="products">Produkte</TabsTrigger>
            <TabsTrigger value="orders">Bestellungen</TabsTrigger>
            <TabsTrigger value="commissions">Provisionen</TabsTrigger>
            <TabsTrigger value="messages">Nachrichten</TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Meine Produkte</CardTitle>
                <Button onClick={() => navigate('/vendor-dashboard/add-product')}>
                  <Plus className="h-4 w-4 mr-2" />
                  Neues Produkt
                </Button>
              </CardHeader>
              <CardContent>
                {products.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Sie haben noch keine Produkte</p>
                    <Button className="mt-4" onClick={() => navigate('/vendor-dashboard/add-product')}>
                      <Plus className="h-4 w-4 mr-2" />
                      Erstes Produkt anlegen
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Select All Row */}
                    {products.length > 1 && (
                      <div className="flex items-center gap-3 p-2 border-b">
                        <button
                          onClick={handleSelectAll}
                          className={`w-6 h-6 rounded border-2 flex items-center justify-center cursor-pointer ${
                            selectedProducts.length === products.length 
                              ? 'bg-blue-500 border-blue-500' 
                              : 'bg-white border-gray-300 hover:border-blue-400'
                          }`}
                        >
                          {selectedProducts.length === products.length && (
                            <Check className="h-4 w-4 text-white" />
                          )}
                        </button>
                        <span className="text-sm text-gray-600">
                          {selectedProducts.length > 0 
                            ? `${selectedProducts.length} ausgewählt` 
                            : 'Alle auswählen'}
                        </span>
                      </div>
                    )}
                    {products.slice(0, 10).map((product) => (
                      <div 
                        key={product.id} 
                        className={`flex items-center gap-4 p-4 border rounded-lg hover:bg-gray-50 ${
                          selectedProducts.includes(product.id) ? 'ring-2 ring-blue-500 bg-blue-50' : ''
                        }`}
                      >
                        {/* Checkbox */}
                        <button
                          onClick={() => handleSelectProduct(product.id)}
                          className={`w-6 h-6 rounded border-2 flex items-center justify-center cursor-pointer flex-shrink-0 ${
                            selectedProducts.includes(product.id) 
                              ? 'bg-blue-500 border-blue-500' 
                              : 'bg-white border-gray-300 hover:border-blue-400'
                          }`}
                        >
                          {selectedProducts.includes(product.id) && (
                            <Check className="h-4 w-4 text-white" />
                          )}
                        </button>
                        <img 
                          src={product.images?.[0]?.startsWith('/api') 
                            ? `${process.env.REACT_APP_BACKEND_URL}${product.images[0]}` 
                            : (product.images?.[0] || '/placeholder.png')} 
                          alt={product.name}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                        <div className="flex-1">
                          <h3 className="font-medium">{product.name}</h3>
                          <p className="text-sm text-gray-500">SKU: {product.sku || '-'}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="font-bold text-green-600">{formatCurrency(product.price)}</span>
                            <Badge className={product.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                              {product.is_active ? 'Aktiv' : 'Inaktiv'}
                            </Badge>
                            <span className="text-xs text-gray-400">Lager: {product.stock || 0}</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Link to={`/vendor-dashboard/products/${product.id}/edit`}>
                            <Button variant="outline" size="sm">
                              <Edit className="h-4 w-4" />
                            </Button>
                          </Link>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => handleDeleteProduct(product.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    {products.length > 10 && (
                      <p className="text-center text-gray-500 text-sm">
                        + {products.length - 10} weitere Produkte
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <Card>
              <CardHeader>
                <CardTitle>Meine Bestellungen</CardTitle>
              </CardHeader>
              <CardContent>
                {orders.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Noch keine Bestellungen eingegangen</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {orders.slice(0, 10).map((order) => (
                      <div key={order.id} className="p-4 border rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-medium">Bestellung #{order.order_number}</p>
                            <p className="text-sm text-gray-500">{formatDate(order.created_at)}</p>
                          </div>
                          {getOrderStatusBadge(order.status)}
                        </div>
                        <div className="mt-2">
                          {order.items?.map((item, idx) => (
                            <div key={idx} className="text-sm text-gray-600">
                              {item.quantity}x {item.name} - {formatCurrency(item.price * item.quantity)}
                            </div>
                          ))}
                        </div>
                        <div className="mt-2 pt-2 border-t flex justify-between">
                          <span className="text-sm text-gray-500">Ihr Anteil:</span>
                          <span className="font-bold">{formatCurrency(order.vendor_subtotal)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Commissions Tab */}
          <TabsContent value="commissions">
            <Card>
              <CardHeader>
                <CardTitle>Meine Provisionen</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-sm text-gray-500">Provisionsrate</p>
                      <p className="text-xl font-bold">{commissionStatus?.commission_rate || 8}%</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Offen</p>
                      <p className="text-xl font-bold text-orange-600">
                        {formatCurrency(commissionStatus?.total_owed)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Perioden</p>
                      <p className="text-xl font-bold">{commissionStatus?.periods?.length || 0}</p>
                    </div>
                  </div>
                </div>

                {commissionStatus?.periods?.length > 0 ? (
                  <div className="space-y-3">
                    {commissionStatus.periods.filter(p => p.status !== 'no_commission').map((period) => (
                      <div key={period.id} className="p-4 border rounded-lg">
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-medium">{period.period_label}</span>
                          <Badge className={
                            period.status === 'paid' ? 'bg-green-100 text-green-800' :
                            period.status === 'overdue' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }>
                            {period.status === 'paid' ? 'Bezahlt' :
                             period.status === 'overdue' ? 'Überfällig' : 'Offen'}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500">Umsatz:</span>
                            <p className="font-medium">{formatCurrency(period.total_sales)}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Provision:</span>
                            <p className="font-medium">{formatCurrency(period.commission_amount)}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Offen:</span>
                            <p className="font-medium text-orange-600">{formatCurrency(period.amount_due)}</p>
                          </div>
                        </div>
                        <p className="text-xs text-gray-400 mt-2">
                          Fällig am: {formatDate(period.due_date)}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                    <p>Keine offenen Provisionen</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <Card>
              <CardHeader>
                <CardTitle>Nachrichten vom Admin</CardTitle>
              </CardHeader>
              <CardContent>
                {commissionStatus?.messages?.length > 0 ? (
                  <div className="space-y-4">
                    {commissionStatus.messages.map((msg) => (
                      <div 
                        key={msg.id} 
                        className={`p-4 border rounded-lg ${
                          msg.message_type === 'reminder' ? 'bg-yellow-50 border-yellow-200' :
                          msg.message_type === 'warning' ? 'bg-orange-50 border-orange-200' :
                          msg.message_type === 'final_notice' ? 'bg-red-50 border-red-200' :
                          'bg-gray-50'
                        } ${!msg.read ? 'border-l-4 border-l-blue-500' : ''}`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center gap-2">
                            {!msg.read && <span className="w-2 h-2 bg-blue-500 rounded-full"></span>}
                            <span className="font-medium">{msg.subject}</span>
                          </div>
                          <span className="text-xs text-gray-400">{formatDate(msg.created_at)}</span>
                        </div>
                        <p className="text-sm whitespace-pre-line">{msg.message}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Keine Nachrichten</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Bulk Actions Toolbar for Products */}
        <BulkActionsToolbar
          selectedCount={selectedProducts.length}
          selectedIds={selectedProducts}
          categories={categories}
          onClearSelection={() => setSelectedProducts([])}
          onSelectAll={handleSelectAll}
          totalCount={products.length}
          allSelected={selectedProducts.length === products.length && products.length > 0}
          onRefresh={loadData}
        />
      </main>
    </div>
  );
};

export default VendorDashboard;
